import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import React, { useRef, useState, useEffect } from 'react';
import { useAnimation } from '../../hooks/useAnimation.es.js';
import { Preview } from './Preview.es.js';
import clsx from 'clsx';
import { getImageUrlFromGoogleUserContent, formatImageUrlQuality } from '../../utils/image.es.js';
import { BreakpointWidthProvider } from '../../contexts/BreakpointWidthContext.es.js';
import { getBreakpointWidthFromStyle } from '../../utils/getBreakpointWidthFromStyle.es.js';
import { Link } from '../Link/Link.es.js';
import { getStyle } from '../../utils/transform.es.js';
import { ImageOptimize } from '../ImageOptimize/ImageOptimize.es.js';
import { mappingAssets } from '../../constants/mapping-assets.es.js';
import { getOffset } from '../../utils/getOffset.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const { defaultImage } = mappingAssets;
const ImageWrapper = styled.div(
  ({ alignment }) => `
    text-align: ${alignment};
  `
);
const funcWrapperImage = ({ commonStyle }) => `
    max-width: 100%;
    display: inline-block;
    transition: transform 0.3s;
    overflow: hidden;
    position: relative;
    object-fit: cover;

    ${getStyle(commonStyle)}
    &:before {
      z-index: 1;
    }
  `;
const StyledImageWrapperDiv = styled.div(funcWrapperImage);
const StyledImageWrapperPicture = styled.picture(funcWrapperImage);
const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: inherit;
  &.preview {
    cursor: zoom-in;
  }
`;
const PlaceholderImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  object-fit: inherit;
`;
const ImageBase = React.forwardRef(
  (props, ref) => {
    var _b, _c;
    const _a = props, {
      src,
      alt = "",
      commonStyle,
      alignment = "center",
      event,
      googleImageParams,
      isEditingMode = false,
      zoomPreview = false,
      fetchPriority = "auto",
      showPlaceholder = true,
      autoOptimize = true
    } = _a, rest = __objRest(_a, [
      "src",
      "alt",
      "commonStyle",
      "alignment",
      "event",
      "googleImageParams",
      "isEditingMode",
      "zoomPreview",
      "fetchPriority",
      "showPlaceholder",
      "autoOptimize"
    ]);
    const imageRef = useRef(null);
    const [showPreview, setShowPreview] = useState(false);
    const [mousePosition, setMousePosition] = useState();
    const handleOpenPreview = (e) => {
      if (!zoomPreview)
        return;
      const { left, top } = getOffset(e.target);
      setMousePosition({
        left,
        top,
        width: e.target.clientWidth
      });
      setShowPreview(true);
    };
    const preProcesssorGoogleImageParams = (googleParams, config) => {
      return __spreadProps(__spreadValues(__spreadValues({}, config), googleParams), {
        width: (googleParams == null ? void 0 : googleParams.percent) ? Math.round((googleParams == null ? void 0 : googleParams.width) * (googleParams == null ? void 0 : googleParams.percent) / 100) : googleParams == null ? void 0 : googleParams.width
      });
    };
    const imageWrapperRef = useRef(null);
    const mergedWrapperRef = ref || imageWrapperRef;
    const { style: animationStyle, animationClassName } = useAnimation(
      commonStyle == null ? void 0 : commonStyle.animation,
      mergedWrapperRef
    );
    const [fullLoaded, setFullLoaded] = useState(false);
    const newSrc = getImageUrlFromGoogleUserContent(
      src,
      preProcesssorGoogleImageParams(googleImageParams, { format: "webp" })
    );
    const pngSrc = getImageUrlFromGoogleUserContent(
      src,
      preProcesssorGoogleImageParams(googleImageParams, { format: "png" })
    );
    const imgError = (image2) => {
      image2.target.src = defaultImage;
      return;
    };
    const placeholderSrc = showPlaceholder ? formatImageUrlQuality(newSrc, 200) : "";
    const allowOptimzeImage = autoOptimize && (googleImageParams == null ? void 0 : googleImageParams.width);
    const visiblePlaceholder = showPlaceholder && !fullLoaded && placeholderSrc !== newSrc;
    const fallbackImage = /* @__PURE__ */ jsx(StyledImage, {
      ref: imageRef,
      src: newSrc,
      alt,
      onError: imgError,
      decoding: "async",
      className: clsx("lazyload", { preview: zoomPreview }),
      loading: fetchPriority === "high" ? "eager" : "lazy",
      fetchpriority: fetchPriority,
      onLoad: () => {
        setFullLoaded(true);
      },
      onClick: handleOpenPreview
    });
    const image = allowOptimzeImage ? /* @__PURE__ */ jsxs(StyledImageWrapperPicture, {
      ref: mergedWrapperRef,
      commonStyle,
      style: animationStyle,
      className: animationClassName,
      children: [
        /* @__PURE__ */ jsx(ImageOptimize, {
          src: newSrc,
          googleImageParams
        }),
        pngSrc && /* @__PURE__ */ jsx("source", {
          srcSet: pngSrc,
          type: "image/png"
        }),
        fallbackImage
      ]
    }) : /* @__PURE__ */ jsxs(StyledImageWrapperDiv, {
      ref: mergedWrapperRef,
      commonStyle,
      style: animationStyle,
      className: animationClassName,
      children: [
        fallbackImage,
        visiblePlaceholder && /* @__PURE__ */ jsx(PlaceholderImage, {
          src: placeholderSrc,
          alt,
          onError: imgError,
          fetchpriority: "high"
        })
      ]
    });
    useEffect(() => {
      if ((imageRef == null ? void 0 : imageRef.current) !== void 0 && imageRef.current.complete !== fullLoaded)
        setFullLoaded(imageRef.current.complete);
    }, [(_b = imageRef == null ? void 0 : imageRef.current) == null ? void 0 : _b.complete, fullLoaded]);
    return /* @__PURE__ */ jsx(BreakpointWidthProvider, {
      sWidth: getBreakpointWidthFromStyle(commonStyle),
      mWidth: getBreakpointWidthFromStyle(commonStyle),
      children: isEditingMode ? image : /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(Link, __spreadProps(__spreadValues({}, event), {
            children: /* @__PURE__ */ jsx(ImageWrapper, __spreadProps(__spreadValues({
              alignment
            }, rest), {
              children: image
            }))
          })),
          /* @__PURE__ */ jsx(Preview, {
            src: ((_c = imageRef.current) == null ? void 0 : _c.currentSrc) || newSrc,
            open: showPreview,
            onClose: () => {
              setShowPreview(false);
            },
            mousePosition
          })
        ]
      })
    });
  }
);

export { ImageBase };
